/**
 * @file auth.store.ts - Store Global de Autenticação (Zustand)
 *
 * Gerencia o estado de autenticação do usuário:
 * - user: Dados do usuário logado
 * - token: JWT token
 * - tenantId: ID do tenant atual
 * - propertyId: ID da filial/propriedade ativa dentro do tenant
 * - isAuthenticated: Flag de autenticação
 *
 * Persistência:
 * - LocalStorage: 'auth-storage' (parcial)
 * - Cookies: auth_token, tenant_id (para interceptors)
 *
 * Actions:
 * - login(): Autentica e persiste credenciais
 * - logout(): Remove credenciais e redireciona
 * - setUser(), setToken(), setTenantId(): Atualizações parciais
 *
 * @see frontend/src/hooks/useAuth.ts
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import Cookies from 'js-cookie';
import { getQueryClient } from '@/lib/query-client';
import type { User, AuthState } from '@/types';

/** Actions disponíveis no store de autenticação */
interface AuthActions {
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setTenantId: (tenantId: number | null) => void;
  setPropertyId: (propertyId: number | null) => void;
  login: (user: User, token: string, tenantId?: number) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
  takeImpersonation: (targetUser: User, targetToken: string) => void;
  leaveImpersonation: () => void;
}

type AuthStore = AuthState & AuthActions;

const COOKIE_OPTIONS = {
  expires: 1,
  path: '/',
  sameSite: 'strict' as const,
  secure: typeof window !== 'undefined' ? window.location.protocol === 'https:' : true,
};

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return atob(padded);
}

export function hasUsableAuthToken(): boolean {
  const token = Cookies.get('auth_token');
  if (!token) return false;

  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return false;

  try {
    const decoded = JSON.parse(decodeBase64Url(parts[1])) as { exp?: number };
    return typeof decoded.exp === 'number' && decoded.exp * 1000 > Date.now() + 5000;
  } catch {
    return false;
  }
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      tenantId: null,
      propertyId: null,
      isAuthenticated: false,
      isLoading: true,
      isImpersonating: false,
      originalUser: null,
      originalToken: null,

      // Actions
      setUser: (user) => set({ user }),

      setToken: (token) => {
        if (token) {
          Cookies.set('auth_token', token, COOKIE_OPTIONS);
        } else {
          Cookies.remove('auth_token', { path: '/' });
        }
        set({ token });
      },

      setTenantId: (tenantId) => {
        if (tenantId) {
          Cookies.set('tenant_id', String(tenantId), COOKIE_OPTIONS);
        } else {
          Cookies.remove('tenant_id', { path: '/' });
        }
        Cookies.remove('property_id', { path: '/' });
        set({ tenantId, propertyId: null });
      },

      setPropertyId: (propertyId) => {
        if (propertyId) {
          Cookies.set('property_id', String(propertyId), COOKIE_OPTIONS);
        } else {
          Cookies.remove('property_id', { path: '/' });
        }
        set({ propertyId });
      },

      login: (user, token, tenantId) => {
        // Limpa a sessão anterior antes de registrar a nova
        getQueryClient().clear();
        Cookies.set('auth_token', token, COOKIE_OPTIONS);
        if (tenantId) {
          Cookies.set('tenant_id', String(tenantId), COOKIE_OPTIONS);
        } else {
          Cookies.remove('tenant_id', { path: '/' });
        }
        Cookies.remove('property_id', { path: '/' });
        set({
          user,
          token,
          tenantId: tenantId ?? user.tenant_id ?? null,
          propertyId: null,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        Cookies.remove('auth_token', { path: '/' });
        Cookies.remove('original_auth_token', { path: '/' });
        Cookies.remove('tenant_id', { path: '/' });
        Cookies.remove('property_id', { path: '/' });
        getQueryClient().clear();
        set({
          user: null,
          token: null,
          tenantId: null,
          propertyId: null,
          isAuthenticated: false,
          isLoading: false,
          isImpersonating: false,
          originalUser: null,
          originalToken: null,
        });
      },

      takeImpersonation: (targetUser: User, targetToken: string) => {
        const current = get();
        // O token original precisa sobreviver a um F5: só o estado do Zustand se perde
        // (isImpersonating/originalToken não eram persistidos), mas o cookie de sessão do
        // usuário-alvo continua valendo, deixando o admin travado sem alerta e sem volta.
        if (current.token) {
          Cookies.set('original_auth_token', current.token, COOKIE_OPTIONS);
        }
        Cookies.set('auth_token', targetToken, COOKIE_OPTIONS);
        if (targetUser.tenant_id) {
          Cookies.set('tenant_id', String(targetUser.tenant_id), COOKIE_OPTIONS);
        }
        Cookies.remove('property_id', { path: '/' });
        getQueryClient().clear();
        set({
          originalUser: current.user,
          originalToken: current.token,
          user: targetUser,
          token: targetToken,
          tenantId: targetUser.tenant_id ?? null,
          propertyId: null,
          isAuthenticated: true,
          isImpersonating: true,
        });
      },

      leaveImpersonation: () => {
        const current = get();
        const originalToken = current.originalToken ?? Cookies.get('original_auth_token') ?? null;
        if (!originalToken || !current.originalUser) return;
        Cookies.set('auth_token', originalToken, COOKIE_OPTIONS);
        Cookies.remove('original_auth_token', { path: '/' });
        Cookies.remove('tenant_id', { path: '/' });
        Cookies.remove('property_id', { path: '/' });
        getQueryClient().clear();
        set({
          user: current.originalUser,
          token: originalToken,
          tenantId: current.originalUser.tenant_id ?? null,
          propertyId: null,
          isAuthenticated: true,
          isImpersonating: false,
          originalUser: null,
          originalToken: null,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        // O token é excluído intencionalmente da persistência no localStorage.
        // O valor fica no cookie auth_token, gerenciado ao definir, iniciar e encerrar a sessão.
        // Isso evita duplicar o JWT em outro armazenamento acessível por XSS.
        tenantId: state.tenantId,
        propertyId: state.propertyId,
        isAuthenticated: state.isAuthenticated,
        // isImpersonating/originalUser precisam sobreviver a um refresh de página, senão o
        // banner "Voltar ao Super Admin" desaparece e o admin fica travado como o usuário
        // impersonado. originalToken fica de fora (é um JWT) e vive no cookie original_auth_token.
        isImpersonating: state.isImpersonating,
        originalUser: state.originalUser,
      }),
    }
  )
);
